import { CookieManager } from "./cookieManager";
import type {
  SessionLifecycleConfig,
  SessionStartMetadata,
  WorkSession,
} from "../types";

const DEFAULT_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

type SessionResponse = { item: WorkSession };

/**
 * Talks only to the server-owned work-session lifecycle endpoints. It never
 * creates client audit events and intentionally sends no browsing or device
 * telemetry.
 */
export class SessionLifecycleClient {
  private static config: SessionLifecycleConfig | null = null;
  private static startingSession: Promise<WorkSession | null> | null = null;

  static configure(config: SessionLifecycleConfig): void {
    this.config = {
      ...config,
      apiBaseUrl: config.apiBaseUrl.replace(/\/$/, ""),
      heartbeatIntervalMs:
        config.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS,
    };
  }

  static isConfigured(): boolean {
    return this.config !== null;
  }

  static getHeartbeatIntervalMs(): number {
    return this.config?.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
  }

  static async ensureSession(
    metadata: SessionStartMetadata = {},
  ): Promise<WorkSession | null> {
    const existingSessionId = CookieManager.getSessionId();
    if (existingSessionId) {
      return { sessionId: existingSessionId, startedAt: "" };
    }

    if (!this.config || !this.getAccessToken()) {
      return null;
    }

    if (!this.startingSession) {
      this.startingSession = this.startSession(metadata).finally(() => {
        this.startingSession = null;
      });
    }

    return this.startingSession;
  }

  static async heartbeatCurrentSession(): Promise<WorkSession | null> {
    const sessionId = CookieManager.getSessionId();
    if (!sessionId) {
      return null;
    }

    return this.request("/auth/sessions/heartbeat", { sessionId });
  }

  static async logoutCurrentSession(): Promise<WorkSession | null> {
    const sessionId = CookieManager.getSessionId();
    if (!sessionId) {
      return null;
    }

    try {
      return await this.request("/auth/sessions/logout", { sessionId });
    } finally {
      // Do not leave a stale server-issued ID in a shared browser cookie when
      // the explicit logout request succeeds or the user is going offline.
      CookieManager.clearSessionId();
    }
  }

  private static async startSession(
    metadata: SessionStartMetadata,
  ): Promise<WorkSession | null> {
    const session = await this.request("/auth/sessions/start", {
      clientClass: metadata.clientClass ?? this.config?.clientClass,
      appVersion: metadata.appVersion ?? this.config?.appVersion,
    });

    if (session) {
      CookieManager.setSessionId(session.sessionId);
    }

    return session;
  }

  private static async request(
    path: string,
    body: Record<string, string | undefined>,
  ): Promise<WorkSession | null> {
    const accessToken = this.getAccessToken();
    if (!this.config || !accessToken || typeof fetch === "undefined") {
      return null;
    }

    try {
      const response = await fetch(`${this.config.apiBaseUrl}${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          Object.fromEntries(
            Object.entries(body).filter(([, value]) => value !== undefined),
          ),
        ),
        credentials: "same-origin",
      });

      if (!response.ok) {
        return null;
      }

      const payload = (await response.json()) as SessionResponse;
      return payload.item;
    } catch {
      // Session telemetry must never prevent a user from logging out or using
      // the CRM while temporarily offline.
      return null;
    }
  }

  /**
   * SSO supplies a cookie, while the two direct-login screens still use the
   * shared local-storage bearer token. Both are authenticated CRM sessions.
   */
  private static getAccessToken(): string | null {
    const cookieToken = CookieManager.getAccessToken();
    if (cookieToken) return cookieToken;
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem("ACCESS_TOKEN")?.replace(/^Bearer\s+/i, "") || null;
  }
}
